import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { isNative, registerNativePush } from "@/lib/native";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Once we know a user is signed in AND we're inside the Capacitor shell,
  // silently register the device's APNs / FCM token with the backend so
  // native push notifications flow to this device.
  useEffect(() => {
    if (!user || user === false) return;
    if (!isNative()) return;
    let cancelled = false;
    (async () => {
      const res = await registerNativePush(api);
      if (cancelled) return;
      if (!res.ok) {
        console.info("[native] push registration:", res.reason);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Magic-link request — backend always returns 200 (privacy: no enumeration).
  const requestLink = async (email) => {
    try {
      const { data } = await api.post("/auth/request-link", { email });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // Exchange a magic-link token for a session cookie.
  // Returns:
  //   { ok: true }                 → signed in successfully
  //   { ok: false, twoFa: {...} }  → 2FA is required — see challenge_id/email
  //   { ok: false, error: "..." }  → generic failure
  const verifyLink = async (token) => {
    try {
      const { data } = await api.post("/auth/verify-link", { token });
      if (data && data.two_fa_required) {
        return { ok: false, twoFa: {
          challenge_id: data.challenge_id, email: data.email,
        }};
      }
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const verifyTwoFa = async (challenge_id, code) => {
    try {
      const { data } = await api.post("/auth/2fa/challenge", { challenge_id, code });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // Password login (admin-only). Same return contract as verifyLink.
  const loginPassword = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login-password", { email, password });
      if (data && data.two_fa_required) {
        return { ok: false, twoFa: {
          challenge_id: data.challenge_id, email: data.email,
        }};
      }
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, requestLink, verifyLink, verifyTwoFa, loginPassword, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
